import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import icPeople from '@/assets/ic_people.svg';
import { Button, Dropdown, Form, InputField } from '@/components';
import useHostbackButton from '@/hooks/useHostbackButton';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import { transferMoneyCheck, transferMoneyConfirm } from '@/services/myAccount';
import { SelectValue } from '@/types';
import {
  DOC_TYPES,
  formatNumberToLocale,
  TRANSFER_BALANCE_REASONS,
  transferBalanceSchema,
  validateFieldError,
} from '@/utils';

type SelectedKeys<T> = Extract<keyof T, `selected${string}`>;
interface Props {
  closeModal: () => void;
}

export const TransferBalance = ({ closeModal }: Props) => {
  const toast = useToast();
  const { pinsData, handleSetPinsData } = usePinsAccount();
  const [targetUser, setTargetUser] = useState<string | null>(null);
  const { isDesktop } = useIsDesktop();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    touched,
    values,
    handleBlur,
    setFieldValue,
    isValid,
  } = useFormik({
    isInitialValid: false,
    initialValues: {
      amount: '',
      docNumber: '',
      selectedTypeDoc: DOC_TYPES[0],
      selectedReason: TRANSFER_BALANCE_REASONS[0],
      txtOtherReason: '',
    },
    validateOnMount: false,
    validateOnBlur: true,
    onSubmit: async (val, { setSubmitting, setFieldError }) => {
      setSubmitting(true);
      if (val.selectedReason.value === 0) {
        setFieldError('selectedReason', 'Debés seleccionar un motivo válido.');
        return setSubmitting(false);
      }
      if (Number(val.amount) > pinsData.accountMoney) {
        toast.show(
          `No tenés saldo suficiente para realizar esta operación. Tu saldo actual es ${pinsData.accountMoney}.`
        );
        return setSubmitting(false);
      }
      const { nombreUsuarioTrans, estado, mensaje } = await transferMoneyCheck({
        nroDoc: Number(val.docNumber),
        tipoDoc: val.selectedTypeDoc.value,
      });
      if (estado && nombreUsuarioTrans) {
        setTargetUser(nombreUsuarioTrans);
      } else {
        toast.show(mensaje);
      }
      setSubmitting(false);
    },
    validationSchema: transferBalanceSchema,
  });

  const handleSelect =
    (field: SelectedKeys<typeof values>) => (e: SelectValue) => {
      setFieldValue(field, e);
    };

  const onChange = (name: string, value: string) => {
    setFieldValue(name, value);
  };

  const handleConfirm = async () => {
    const response = await transferMoneyConfirm({
      montoATransf: Number(values.amount),
      nroDoc: Number(values.docNumber),
      tipoDoc: values.selectedTypeDoc.value,
      opcionMotivo: values.selectedReason.value,
      txtMotivoOtro: values.txtOtherReason,
    });
    if (!response.estado) {
      toast.show(response.mensaje);
    } else {
      toast.show('El saldo se transfirió correctamente.');
      handleSetPinsData({
        ...pinsData,
        accountMoney:
          response.actualizacionCuenta?.saldo ??
          pinsData.accountMoney - Number(values.amount),
        pinsAvailable: response.actualizacionCuenta?.pinesDisponibles ?? 0,
        showModalTransferBalance: false,
      });
      queryClient.invalidateQueries('init-my-account');
    }
  };

  useEffect(() => {
    navigate('', { replace: false });
  }, []);

  const handleBackButton = useCallback(() => {
    closeModal();
  }, [closeModal]);
  useHostbackButton(handleBackButton);

  return (
    <div>
      <div className="flex justify-center items-center gap-3 mb-5">
        <img alt="Icon" className="w-6" src={icPeople} />
        <h2 className="text-xl font-bold text-center">
          Transferí saldo a otro DT
        </h2>
      </div>
      {targetUser ? (
        <>
          <p className="font-medium">
            Vas a transferir{' '}
            <span className="text-gdt-blue">
              ${formatNumberToLocale(values.amount)}
            </span>{' '}
            a <span className="text-gdt-blue">{targetUser},</span> DNI{' '}
            {values.docNumber}
          </p>
          <Button
            className="w-full mt-4"
            type="button"
            variant="primary"
            onClick={handleConfirm}
          >
            Confirmar
          </Button>
        </>
      ) : (
        <Form.Form
          className="px-1"
          loading={isSubmitting}
          onSubmit={handleSubmit}
        >
          <Form.Field>
            <InputField
              autoComplete="off"
              behavior="currency"
              error={validateFieldError(
                touched.amount,
                errors.amount,
                values.amount.length
              )}
              label="Monto a transferir"
              maskConfig={{
                thousandSeparator: '.',
              }}
              maxLength={11}
              minLength={1}
              name="amount"
              placeholder="Numero"
              success={!errors.amount && values.amount.length > 0}
              type="text"
              value={values.amount}
              onBlur={handleBlur}
              onChange={onChange}
            />
          </Form.Field>

          <div className="flex flex-col mt-4">
            <label className="text-sm font-medium" htmlFor="docNumber">
              Documento del receptor
            </label>
            <div className="grid grid-cols-12 gap-x-3 mt-2">
              <div className="col-span-4 sm:col-span-3">
                <Dropdown
                  options={DOC_TYPES}
                  selected={values.selectedTypeDoc}
                  onChange={handleSelect('selectedTypeDoc')}
                />
              </div>
              <div className=" col-span-8 sm:col-span-9">
                <InputField
                  autoComplete="off"
                  behavior="currency"
                  error={validateFieldError(
                    touched.docNumber,
                    errors.docNumber,
                    values.docNumber.length
                  )}
                  id="docNumber"
                  maskConfig={{
                    thousandSeparator: '.',
                  }}
                  maxLength={11}
                  minLength={2}
                  name="docNumber"
                  placeholder="Número"
                  success={isValid && values.docNumber.length > 0}
                  value={values.docNumber}
                  onBlur={handleBlur}
                  onChange={onChange}
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Dropdown
              error={validateFieldError(
                touched.selectedReason?.value,
                errors.selectedReason?.value,
                values.selectedReason.label.length
              )}
              label="Motivo de la transferencia"
              options={TRANSFER_BALANCE_REASONS}
              placement={isDesktop ? 'bottom' : 'top'}
              selected={values.selectedReason}
              withFloat={isDesktop}
              onChange={handleSelect('selectedReason')}
            />
          </div>
          <div className="mt-4 mb-6">
            {values.selectedReason.value === 3 && (
              <InputField
                autoComplete="off"
                behavior="default"
                error={validateFieldError(
                  touched.txtOtherReason,
                  errors.txtOtherReason,
                  values.txtOtherReason.length
                )}
                label="Otro motivo"
                maxLength={20}
                name="txtOtherReason"
                placeholder="Ingresar"
                value={values.txtOtherReason}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            )}
          </div>
          <Button
            className="w-full"
            disabled={
              !isValid || isSubmitting || values.selectedReason.value === 0
            }
            type="submit"
            variant="primary"
          >
            Continuar
          </Button>
        </Form.Form>
      )}
    </div>
  );
};
